import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    AlertTriangle,
    ArrowLeft,
    CheckCircle,
    ClipboardList,
    Printer,
    Save,
    Send,
    X,
} from 'lucide-react';
import {
    reviewerEvaluationService,
    type ReviewerEvaluationAssignmentDetail,
    type ReviewerEvaluationCriteria,
    type CreateUpdateReviewerEvaluationPayload,
    type EvaluationResultEnum, 
} from '../services/adminAuth';
import { useNotification } from '../../sharedComponents/Notifications';

const REVIEWER_EVAL_PRINT_STYLES = `
@media print {
    @page { size: A4; margin: 14mm 12mm; }
    body, html { margin: 0 !important; padding: 0 !important; background: white !important; }
    aside, nav { display: none !important; }
    .fixed { display: none !important; }
    .w-full.max-w-6xl { max-width: 100% !important; margin: 0 !important; padding: 0 !important; }
    .rev-eval-print-doc, .rev-eval-print-doc * {
        color: #000 !important;
        background: transparent !important;
        box-shadow: none !important;
    }
    .rev-eval-print-doc { background: white !important; }
    .rev-eval-print-section { page-break-inside: avoid; break-inside: avoid; }
    h1, h2, h3, h4 { break-after: avoid; page-break-after: avoid; }
}
`;

// ── Constants ─────────────────────────────────────────────────────────────────

const ZERO_GUID = '00000000-0000-0000-0000-000000000000';

/**
 * EvaluationType enum (mirrors backend)
 * Title = 1, OverviewContentStructure = 2, Scoring = 3,
 * EditorialGrammar = 4, ReferenceBibliography = 5
 */
const EvaluationType = {
    Title: 1,
    OverviewContentStructure: 2,
    Scoring: 3,
    EditorialGrammar: 4,
    ReferenceBibliography: 5,
} as const;
type EvaluationTypeValue = typeof EvaluationType[keyof typeof EvaluationType];

/**
 * EvaluationResult enum (mirrors backend)
 * Yes = 1, No = 2, Partially = 3
 */
const EvaluationResult = { Yes: 1, No: 2, Partially: 3 } as const;
type EvaluationResultValue = 1 | 2 | 3 | null;

const CRITERIA_SECTIONS: {
    type: EvaluationTypeValue;
    badge: string;
    label: string;
    question: string;
    allowPartially: boolean;
}[] = [
    {
        type: EvaluationType.Title,
        badge: 'A',
        label: 'Title',
        question: "Is reviewer's comment on the title satisfactory?",
        allowPartially: false,
    },
    {
        type: EvaluationType.OverviewContentStructure,
        badge: 'B',
        label: 'Overview of the Content and Structure',
        question: "Are reviewer's comments on this satisfactory?",
        allowPartially: false,
    },
    {
        type: EvaluationType.Scoring,
        badge: 'C',
        label: 'Scoring',
        question: "Is reviewer's scoring objective and in agreement with general assessment?",
        allowPartially: false,
    },
    {
        type: EvaluationType.EditorialGrammar,
        badge: 'D',
        label: 'Editorial / Grammar',
        question: "Are reviewer's comments on these satisfactory?",
        allowPartially: false,
    },
    {
        type: EvaluationType.ReferenceBibliography,
        badge: 'E',
        label: 'Reference / Bibliography',
        question: "Are reviewer's comments satisfactory?",
        allowPartially: false,
    },
];

// ── Local state shapes ────────────────────────────────────────────────────────

interface CriteriaState {
    id: string;
    type: EvaluationTypeValue;
    result: EvaluationResultValue;
    comment: string;
}

interface ChapterState {
    id: string;
    chapterNumber: number;
    result: EvaluationResultValue;
    comment: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const outcomeNumToLabel = (v: number | null | undefined): string => {
    if (v === 1) return 'YES';
    if (v === 2) return 'NO';
    return '';
};

const labelToOutcomeNum = (label: string): number =>
    label === 'YES' ? 1 : label === 'NO' ? 2 : 1;

const buildInitialCriteria = (): CriteriaState[] =>
    CRITERIA_SECTIONS.map(s => ({ id: ZERO_GUID, type: s.type, result: null, comment: '' }));

const mergeCriteriaFromApi = (
    base: CriteriaState[],
    apiCriteria: ReviewerEvaluationCriteria[]
): CriteriaState[] =>
    base.map(c => {
        const match = apiCriteria.find(a => a.type === c.type);
        if (!match) return c;
        return { id: match.id ?? ZERO_GUID, type: c.type, result: match.result as EvaluationResultValue, comment: match.comment ?? '' };
    });

// ── Validation ────────────────────────────────────────────────────────────────

const validateForSubmit = (criteria: CriteriaState[], chapters: ChapterState[], generalComments: string): string[] => {
    const missing: string[] = [];
    for (const c of criteria) {
        if (!c.result) {
            const section = CRITERIA_SECTIONS.find(s => s.type === c.type);
            missing.push(`${section?.badge}. ${section?.label}`);
        }
    }
    chapters.forEach(ch => {
        if (!ch.result) missing.push(`Chapter ${ch.chapterNumber} — satisfactory?`);
    });
    if (!generalComments.trim()) missing.push('G. General Comments');
    return missing;
};

// ── Reusable primitives ───────────────────────────────────────────────────────

const ROField = ({ label, value, fullWidth = false }: { label: string; value: string; fullWidth?: boolean }) => (
    <div className={fullWidth ? 'col-span-full' : ''}>
        <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1">{label}</p>
        <div className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-sm font-medium text-gray-800 min-h-[38px]">
            {value || <span className="italic text-gray-400">—</span>}
        </div>
    </div>
);

const ROTextArea = ({ label, value, rows = 3 }: { label?: string; value: string; rows?: number }) => (
    <div>
        {label && <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1">{label}</p>}
        <div
            className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-sm font-medium text-gray-800"
            style={{ minHeight: `${rows * 1.75}rem` }}
        >
            {value || <span className="italic text-gray-400">—</span>}
        </div>
    </div>
);

const TextArea = ({
    label, value, onChange, rows = 3, placeholder = '',
}: { label?: string; value: string; onChange: (v: string) => void; rows?: number; placeholder?: string }) => (
    <div>
        {label && <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1">{label}</p>}
        <textarea
            rows={rows}
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#088A61] focus:border-transparent resize-none transition-all"
        />
    </div>
);

const ResultRadio = ({
    name, value, onChange, allowPartially = false, readOnly = false,
}: {
    name: string; value: EvaluationResultValue; onChange: (v: EvaluationResultValue) => void;
    allowPartially?: boolean; readOnly?: boolean;
}) => {
    const options: { label: string; val: EvaluationResultValue }[] = [
        { label: 'Yes', val: EvaluationResult.Yes },
        { label: 'No', val: EvaluationResult.No },
        ...(allowPartially ? [{ label: 'Partially', val: EvaluationResult.Partially as EvaluationResultValue }] : []),
    ];
    return (
        <div className="flex gap-6 flex-wrap">
            {options.map(opt => (
                <label key={opt.label} className={`flex items-center gap-2 ${readOnly ? 'opacity-70 cursor-default' : 'cursor-pointer'}`}>
                    <input
                        type="radio"
                        name={name}
                        checked={value === opt.val}
                        onChange={() => !readOnly && onChange(opt.val)}
                        readOnly={readOnly}
                        className="accent-[#088A61] w-4 h-4"
                    />
                    <span className="text-sm font-semibold text-gray-700">{opt.label}</span>
                </label>
            ))}
        </div>
    );
};

const YesNoRadio = ({
    name, value, onChange, readOnly = false,
}: { name: string; value: string; onChange: (v: string) => void; readOnly?: boolean }) => (
    <div className="flex gap-6 flex-wrap">
        {['YES', 'NO'].map(opt => (
            <label key={opt} className={`flex items-center gap-2 ${readOnly ? 'opacity-70 cursor-default' : 'cursor-pointer'}`}>
                <input
                    type="radio"
                    name={name}
                    checked={value === opt}
                    onChange={() => !readOnly && onChange(opt)}
                    readOnly={readOnly}
                    className="accent-[#088A61] w-4 h-4"
                />
                <span className="text-sm font-semibold text-gray-700">{opt}</span>
            </label>
        ))}
    </div>
);

const SectionCard = ({ title, accent = false, children }: {
    title?: string; accent?: boolean; children: React.ReactNode;
}) => (
    <div className={`rounded-xl border p-5 space-y-4 ${accent ? 'border-emerald-200 bg-emerald-50/40' : 'border-gray-100 bg-white'}`}>
        {title && (
            <div className={`flex items-center gap-2 pb-3 border-b ${accent ? 'border-emerald-200' : 'border-gray-100'}`}>
                <div className={`w-1 h-4 rounded-full ${accent ? 'bg-[#088A61]' : 'bg-gray-300'}`} />
                <h3 className={`text-[11px] font-black uppercase tracking-widest ${accent ? 'text-[#088A61]' : 'text-gray-500'}`}>{title}</h3>
            </div>
        )}
        {children}
    </div>
);

// ── Confirm Modal ─────────────────────────────────────────────────────────────

interface ConfirmModalProps {
    isOpen: boolean; onClose: () => void; mode: 'draft' | 'submit';
    onConfirmDraft: () => void; onConfirmSubmit: () => void;
    isSavingDraft: boolean; isSavingSubmit: boolean; manuscriptTitle: string;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
    isOpen, onClose, mode, onConfirmDraft, onConfirmSubmit, isSavingDraft, isSavingSubmit, manuscriptTitle,
}) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={!isSavingDraft && !isSavingSubmit ? onClose : undefined} />
            <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className={`px-6 py-5 ${mode === 'submit' ? 'bg-gradient-to-r from-[#088A61] to-[#065f44]' : 'bg-gradient-to-r from-gray-700 to-gray-800'}`}>
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                                {mode === 'submit' ? <Send className="w-5 h-5 text-white" /> : <Save className="w-5 h-5 text-white" />}
                            </div>
                            <div>
                                <h3 className="text-base font-black text-white">
                                    {mode === 'submit' ? 'Submit Evaluation Report' : 'Save as Draft'}
                                </h3>
                                <p className="text-xs text-white/70 mt-0.5">
                                    {mode === 'submit' ? 'This action will finalise your evaluation' : 'Your progress will be saved'}
                                </p>
                            </div>
                        </div>
                        {!isSavingDraft && !isSavingSubmit && (
                            <button onClick={onClose} className="text-white/60 hover:text-white transition-colors flex-shrink-0">
                                <X className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                </div>
                <div className="px-6 py-5 space-y-4">
                    <div className="bg-gray-50 border border-gray-100 rounded-lg px-4 py-3">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-0.5">Manuscript</p>
                        <p className="text-sm font-semibold text-gray-800 leading-snug line-clamp-2">{manuscriptTitle}</p>
                    </div>
                    {mode === 'submit' ? (
                        <>
                            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                <p className="text-xs text-amber-700 font-medium leading-relaxed">
                                    Submitting will finalise your evaluation report. Make sure all required fields are filled before proceeding.
                                </p>
                            </div>
                            <div className="flex flex-col gap-2 pt-1">
                                <button onClick={onConfirmSubmit} disabled={isSavingSubmit || isSavingDraft}
                                    className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-[#088A61] text-white rounded-xl text-sm font-bold hover:bg-[#067451] transition-colors shadow-md disabled:opacity-60 disabled:cursor-not-allowed">
                                    {isSavingSubmit
                                        ? <><div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />Submitting…</>
                                        : <><Send className="w-4 h-4" />Submit Evaluation</>}
                                </button>
                                <button onClick={onConfirmDraft} disabled={isSavingDraft || isSavingSubmit}
                                    className="w-full flex items-center justify-center gap-2 px-5 py-3 border border-gray-200 text-gray-600 bg-white rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                                    {isSavingDraft
                                        ? <><div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />Saving…</>
                                        : <><Save className="w-4 h-4" />Save as Draft Instead</>}
                                </button>
                                <button onClick={onClose} disabled={isSavingDraft || isSavingSubmit}
                                    className="w-full px-5 py-2.5 text-gray-400 text-sm font-medium hover:text-gray-600 transition-colors disabled:opacity-60">
                                    Cancel
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
                                <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                <p className="text-xs text-blue-700 font-medium leading-relaxed">
                                    Your form data will be saved as a draft. You can return and complete it at any time before submitting.
                                </p>
                            </div>
                            <div className="flex items-center gap-3 pt-1">
                                <button onClick={onClose} disabled={isSavingDraft}
                                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 font-semibold hover:bg-gray-50 transition-colors disabled:opacity-60">
                                    Cancel
                                </button>
                                <button onClick={onConfirmDraft} disabled={isSavingDraft}
                                    className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 bg-gray-800 text-white rounded-xl text-sm font-bold hover:bg-gray-900 transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed">
                                    {isSavingDraft
                                        ? <><div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />Saving…</>
                                        : <><Save className="w-4 h-4" />Save Draft</>}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// ── Page ──────────────────────────────────────────────────────────────────────

const ReviewerEvaluationForm: React.FC = () => {
    const { assignmentId } = useParams<{ assignmentId: string }>();
    const navigate = useNavigate();
    const { showNotification } = useNotification();

    const [detail, setDetail] = useState<ReviewerEvaluationAssignmentDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [savingDraft, setSavingDraft] = useState(false);
    const [savingSubmit, setSavingSubmit] = useState(false);
    const [confirmModal, setConfirmModal] = useState<'draft' | 'submit' | null>(null);

    // ── Form state ────────────────────────────────────────────────────────────
    const [criteria, setCriteria] = useState<CriteriaState[]>(buildInitialCriteria());
    const [chapters, setChapters] = useState<ChapterState[]>([]);
    const [generalComments, setGeneralComments] = useState('');
    const [processFurtherToAuthor, setProcessFurtherToAuthor] = useState('');
    const [returnToReviewer, setReturnToReviewer] = useState('');
    const [attachCommentsForAuthor, setAttachCommentsForAuthor] = useState('');

    const isSubmitted = detail?.reviewerEvaluation?.submitted === true;

    // ── Load ──────────────────────────────────────────────────────────────────

    const loadDetail = React.useCallback(async () => {
        if (!assignmentId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const res = await reviewerEvaluationService.getAssignedManuscriptById(assignmentId);
            if (res?.isSuccess && res.data) {
                const d = res.data;
                setDetail(d);

                // Build chapter slots from the reviewer's manuscript chapters
                const reviewChapters = d.manuscriptReview?.chapters ?? [];

                const ev = d.reviewerEvaluation;
                if (ev) {
                    // Merge saved criteria
                    setCriteria(prev => mergeCriteriaFromApi(prev, ev.criteria ?? []));

                    // Merge saved chapter evaluations
                    const mergedChapters: ChapterState[] = reviewChapters.map(ch => {
                        const evCh = (ev.chapters ?? []).find(c => c.chapterNumber === ch.chapterNumber);
                        return {
                            id: evCh?.id ?? ZERO_GUID,
                            chapterNumber: ch.chapterNumber,
                            result: evCh ? (evCh.result as EvaluationResultValue) : null,
                            comment: evCh?.comment ?? '',
                        };
                    })
                    .sort((a, b) => a.chapterNumber - b.chapterNumber);
                    setChapters(mergedChapters);

                    setGeneralComments(ev.generalComments ?? '');
                    setProcessFurtherToAuthor(outcomeNumToLabel(ev.processFurtherToAuthor));
                    setReturnToReviewer(outcomeNumToLabel(ev.returnToReviewer));
                    setAttachCommentsForAuthor(outcomeNumToLabel(ev.attachCommentsForAuthor));
                } else {
                    // No saved evaluation yet — just set up empty chapter slots
                    setChapters(
                        reviewChapters
                            .map(ch => ({ id: ZERO_GUID, chapterNumber: ch.chapterNumber, result: null, comment: '' }))
                            .sort((a, b) => a.chapterNumber - b.chapterNumber) 
                    );
                }
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: res?.message || 'Failed to load evaluation assignment',
                });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'Failed to load evaluation assignment' });
        } finally {
            setLoading(false);
        }
    }, [assignmentId]);

    useEffect(() => { loadDetail(); }, [loadDetail]);

    // ── State mutators ────────────────────────────────────────────────────────

    const setCriteriaResult = (type: EvaluationTypeValue, result: EvaluationResultValue) =>
        setCriteria(prev => prev.map(c => c.type === type ? { ...c, result } : c));

    const setCriteriaComment = (type: EvaluationTypeValue, comment: string) =>
        setCriteria(prev => prev.map(c => c.type === type ? { ...c, comment } : c));

    const setChapterResult = (idx: number, result: EvaluationResultValue) =>
        setChapters(prev => prev.map((ch, i) => i === idx ? { ...ch, result } : ch));

    const setChapterComment = (idx: number, comment: string) =>
        setChapters(prev => prev.map((ch, i) => i === idx ? { ...ch, comment } : ch));

    const handleOutcomeChange = (field: 'processFurther' | 'returnReviewer', value: string) => {
        if (field === 'processFurther') {
            setProcessFurtherToAuthor(value);
            setReturnToReviewer(value === 'YES' ? 'NO' : 'YES');
        } else {
            setReturnToReviewer(value);
            setProcessFurtherToAuthor(value === 'YES' ? 'NO' : 'YES');
        }
    };

    // ── Build payload ─────────────────────────────────────────────────────────

    const buildPayload = (submit: boolean): CreateUpdateReviewerEvaluationPayload => ({
        id: detail?.reviewerEvaluation?.id ?? ZERO_GUID,
        reviewerEvaluationAssignmentId: assignmentId ?? ZERO_GUID,
        criteria: criteria
            .filter(c => c.result !== null)
            .map(c => ({ id: c.id, type: c.type, result: c.result as EvaluationResultEnum, comment: c.comment })),
        chapters: chapters.map(ch => ({
            id: ch.id,
            chapterNumber: ch.chapterNumber,
            chapterTitle: '',          // not used/shown — always empty
            comment: ch.comment,
            result: ch.result ?? EvaluationResult.Yes,
        })),
        decision: 1,                   // always 1 — backend field, not surfaced in UI
        generalComments,
        processFurtherToAuthor: labelToOutcomeNum(processFurtherToAuthor),
        returnToReviewer: labelToOutcomeNum(returnToReviewer),
        attachCommentsForAuthor: 1,
        // attachCommentsForAuthor: labelToOutcomeNum(attachCommentsForAuthor),
        submit,
    });

    // ── Save / Submit ─────────────────────────────────────────────────────────

    const executeSaveDraft = async () => {
        setSavingDraft(true);
        await new Promise(r => setTimeout(r, 0));
        try {
            const res = await reviewerEvaluationService.createUpdateReviewerEvaluation(buildPayload(false));
            if (res?.isSuccess) {
                showNotification('success', { title: 'Draft Saved', message: 'Your progress has been saved as a draft.' });
                setConfirmModal(null);
                await loadDetail();
            } else {
                showNotification('error', { title: 'Error', message: res?.message || 'Failed to save draft. Please try again.' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred while saving draft.' });
        } finally {
            setSavingDraft(false);
        }
    };

    const executeSubmit = async () => {
        setSavingSubmit(true);
        await new Promise(r => setTimeout(r, 0));
        try {
            const res = await reviewerEvaluationService.createUpdateReviewerEvaluation(buildPayload(true));
            if (res?.isSuccess) {
                showNotification('success', { title: 'Submitted', message: 'Evaluation report submitted successfully.' });
                setConfirmModal(null);
                navigate(-1);
            } else {
                showNotification('error', { title: 'Submission Failed', message: res?.message || 'Failed to submit. Please try again.' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred while submitting.' });
        } finally {
            setSavingSubmit(false);
        }
    };

    const handleOpenSubmitModal = () => {
        const errors = validateForSubmit(criteria, chapters, generalComments);
        if (errors.length > 0) {
            showNotification('error', {
                title: 'Incomplete Form',
                message: `Please fill in the following before submitting: ${errors.join(', ')}.`,
            });
            return;
        }
        setConfirmModal('submit');
    };

    // ── Derived display values ────────────────────────────────────────────────

    const submission = (detail as any)?.manuscriptReview?.submission ?? (detail as any)?.submission ?? null;
    const reviewAss = (detail as any)?.manuscriptReview?.reviewAssignment ?? (detail as any)?.reviewAssignment ?? null;
    const titleDisplay  = submission?.title ?? '—';
    const authorName    = submission?.author?.person?.fullName ?? '—';
    const submissionNumber = submission?.submissionNumber ?? '—';
    const reviewerName  = reviewAss?.reviewer?.person?.fullName ?? '—';
    const areaField     = submission?.category?.name ?? '—';

    // ── Loading ───────────────────────────────────────────────────────────────

    if (loading) return (
        <div className="flex h-96 items-center justify-center">
            <div className="w-10 h-10 border-4 border-gray-100 border-t-[#088A61] rounded-full animate-spin" />
        </div>
    );

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="w-full max-w-6xl mx-auto px-4 pb-24">
            <style>{REVIEWER_EVAL_PRINT_STYLES}</style>

            <ConfirmModal
                isOpen={confirmModal !== null}
                onClose={() => !savingDraft && !savingSubmit && setConfirmModal(null)}
                mode={confirmModal ?? 'draft'}
                onConfirmDraft={executeSaveDraft}
                onConfirmSubmit={executeSubmit}
                isSavingDraft={savingDraft}
                isSavingSubmit={savingSubmit}
                manuscriptTitle={titleDisplay}
            />

            <div className="print:hidden">
            {/* ── Hero Header ───────────────────────────────────────────────── */}
            <div className="mb-8">
                <button onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700 font-medium transition-colors mb-6 group">
                    <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                    Back
                </button>

                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#088A61] to-[#065f44] px-8 py-7 shadow-lg">
                    <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/5" />
                    <div className="absolute -bottom-10 -right-2 w-56 h-56 rounded-full bg-white/5" />
                    <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
                                <ClipboardList className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <p className="text-[10px] font-bold text-emerald-200 uppercase tracking-widest mb-0.5">
                                    Tertiary Education Trust Fund — TAG
                                </p>
                                <h1 className="text-xl sm:text-2xl font-black text-white leading-tight">
                                    Evaluation of Reviewer's Assessment Report
                                </h1>
                                <p className="text-base font-bold text-white/80 mt-0.5 line-clamp-1">{titleDisplay}</p>
                            </div>
                        </div>
                        <button onClick={() => window.print()}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors border border-white/20 self-start sm:self-center">
                            <Printer className="w-4 h-4" /> Print
                        </button>
                    </div>
                    <div className="relative mt-4 flex flex-wrap gap-3">
                        {[
                            { label: 'Submission No.', value: submissionNumber },
                            { label: 'Reviewer', value: reviewerName },
                            ...(isSubmitted ? [{ label: 'Status', value: 'Submitted' }] : []),
                        ].map(item => (
                            <div key={item.label} className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-1.5 border border-white/15">
                                <span className="text-[10px] font-bold text-emerald-300 uppercase">{item.label}</span>
                                <span className="text-xs font-bold text-white">{item.value}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {isSubmitted && (
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3 mb-4">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <p className="text-[11px] font-bold text-green-700 uppercase tracking-wide">
                        This evaluation report has been submitted and is now read-only.
                    </p>
                </div>
            )}

            {/* ── Form body ─────────────────────────────────────────────────── */}
            <div id="printContentRev" className="space-y-5">

                {/* 1 — Manuscript Identification */}
                <SectionCard title="Manuscript Identification">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <ROField label="Name of Author" value={authorName} />
                        <ROField label="Manuscript Number" value={submissionNumber} />
                        <ROField label="Title of Manuscript" value={titleDisplay} fullWidth />
                        <ROField label="Reviewer's Name" value={reviewerName} />
                        <ROField label="Manuscript's Area / Field" value={areaField} />
                    </div>
                </SectionCard>

                {/* 2 — Evaluation Section */}
                <SectionCard title="Evaluation Section" accent>
                    <div className="space-y-6">

                        {/* A, B, D, E, F — criteria */}
                        {CRITERIA_SECTIONS.map((section, si) => {
                            const state = criteria.find(c => c.type === section.type)!;
                            return (
                                <React.Fragment key={section.type}>
                                    {si > 0 && <div className="border-t border-emerald-100" />}
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2">
                                            <span className="w-6 h-6 rounded-full bg-[#088A61] text-white text-[10px] font-black flex items-center justify-center flex-shrink-0">
                                                {section.badge}
                                            </span>
                                            <p className="text-sm font-black text-gray-700 uppercase tracking-wide">{section.label}</p>
                                        </div>
                                        <p className="text-xs text-gray-500 pl-8">{section.question}</p>
                                        <div className="pl-8">
                                            <ResultRadio
                                                name={`criteria-${section.type}`}
                                                value={state.result}
                                                onChange={v => setCriteriaResult(section.type, v)}
                                                allowPartially={section.allowPartially}
                                                readOnly={isSubmitted}
                                            />
                                        </div>
                                        {isSubmitted ? (
                                            <ROTextArea label="Comment where necessary" value={state.comment} rows={3} />
                                        ) : (
                                            <TextArea
                                                label="Comment where necessary"
                                                value={state.comment}
                                                onChange={v => setCriteriaComment(section.type, v)}
                                                rows={3}
                                                placeholder="Enter your comments…"
                                            />
                                        )}
                                    </div>
                                </React.Fragment>
                            );
                        })}

                        {/* C — Chapter by Chapter (repeated per chapter from API) */}
                        {chapters.length > 0 && (
                            <>
                                <div className="border-t border-emerald-100" />
                                <div className="space-y-4">
                                    <div className="flex items-center gap-2">
                                        <span className="w-6 h-6 rounded-full bg-[#088A61] text-white text-[10px] font-black flex items-center justify-center flex-shrink-0">F</span>
                                        <p className="text-sm font-black text-gray-700 uppercase tracking-wide">Chapter by Chapter Assessment</p>
                                    </div>
                                    <div className="space-y-4 pl-2">
                                        {chapters.map((ch, idx) => (
                                            <div key={idx} className="rounded-lg border border-emerald-100 bg-white p-4 space-y-3">
                                                <p className="text-xs font-black text-emerald-700 uppercase tracking-wide">
                                                    Chapter {ch.chapterNumber}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    Are reviewer's comments on this chapter and its sub-topics satisfactory?
                                                </p>
                                                <ResultRadio
                                                    name={`chapter-${idx}`}
                                                    value={ch.result}
                                                    onChange={v => setChapterResult(idx, v)}
                                                    allowPartially
                                                    readOnly={isSubmitted}
                                                />
                                                {isSubmitted ? (
                                                    <ROTextArea label="Comment where necessary" value={ch.comment} rows={2} />
                                                ) : (
                                                    <TextArea
                                                        label="Comment where necessary"
                                                        value={ch.comment}
                                                        onChange={v => setChapterComment(idx, v)}
                                                        rows={2}
                                                        placeholder="Comments on this chapter's review…"
                                                    />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}

                        {/* G — General Comments */}
                        <div className="border-t border-emerald-100" />
                        <div className="space-y-3">
                            <div className="flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-[#088A61] text-white text-[10px] font-black flex items-center justify-center flex-shrink-0">G</span>
                                <p className="text-sm font-black text-gray-700 uppercase tracking-wide">General Comments</p>
                            </div>
                            {isSubmitted ? (
                                <ROTextArea value={generalComments} rows={5} />
                            ) : (
                                <TextArea
                                    value={generalComments}
                                    onChange={setGeneralComments}
                                    rows={5}
                                    placeholder="Enter general comments on the reviewer's overall assessment report…"
                                />
                            )}
                        </div>

                    </div>
                </SectionCard>

                {/* 3 — Outcome */}
                <SectionCard title="Outcome / Disposition">
                    <div className="space-y-5">
                        <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
                            <AlertTriangle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-blue-700 leading-relaxed">
                                <span className="font-bold">These two options are mutually exclusive.</span> Selecting <span className="font-semibold">Yes</span> for one automatically sets the other to <span className="font-semibold">No</span>, and vice versa. Only one outcome can apply at a time.
                            </p>
                        </div>
                        {([
                            { label: 'Process further to Author', value: processFurtherToAuthor, name: 'processFurther', onChange: (v: string) => handleOutcomeChange('processFurther', v) },
                            { label: 'Return to Reviewer',        value: returnToReviewer,       name: 'returnReviewer', onChange: (v: string) => handleOutcomeChange('returnReviewer', v) },
                            // { label: "Attach this Comments for Author's Consideration", value: attachCommentsForAuthor, set: setAttachCommentsForAuthor, name: 'attachComments' },
                        ] as const).map(item => (
                            <div key={item.name} className="space-y-2">
                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider">{item.label}</p>
                                <YesNoRadio name={item.name} value={item.value} onChange={item.onChange} readOnly={isSubmitted} />
                            </div>
                        ))}
                    </div>
                </SectionCard>

            </div>

            {/* ── Sticky footer ─────────────────────────────────────────────── */}
            {!isSubmitted && (
                <div className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-100 px-6 py-3 flex justify-end gap-3 shadow-lg">
                    <button onClick={() => navigate(-1)} disabled={savingDraft || savingSubmit}
                        className="px-5 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 font-medium transition-colors disabled:opacity-50">
                        Cancel
                    </button>
                    <button onClick={() => setConfirmModal('draft')} disabled={savingDraft || savingSubmit}
                        className="flex items-center gap-2 px-5 py-2 border border-[#088A61] text-[#088A61] bg-white rounded-lg text-sm font-bold hover:bg-emerald-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                        <Save className="w-4 h-4" /> Save Draft
                    </button>
                    <button onClick={handleOpenSubmitModal} disabled={savingDraft || savingSubmit}
                        className="flex items-center gap-2 px-6 py-2 bg-[#088A61] text-white rounded-lg text-sm font-bold hover:bg-[#067451] transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed">
                        <Send className="w-4 h-4" /> Submit Evaluation
                    </button>
                </div>
            )}
            </div>

            <ReviewerEvalPrintView
                authorName={authorName}
                submissionNumber={submissionNumber}
                titleDisplay={titleDisplay}
                reviewerName={reviewerName}
                areaField={areaField}
                criteria={criteria}
                chapters={chapters}
                generalComments={generalComments}
                processFurtherToAuthor={processFurtherToAuthor}
                returnToReviewer={returnToReviewer}
            />
        </div>
    );
};

// ── Print-only view ──────────────────────────────────────────────────────────

interface ReviewerEvalPrintViewProps {
    authorName: string;
    submissionNumber: string;
    titleDisplay: string;
    reviewerName: string;
    areaField: string;
    criteria: CriteriaState[];
    chapters: ChapterState[];
    generalComments: string;
    processFurtherToAuthor: string;
    returnToReviewer: string;
}

const resultLabel = (r: EvaluationResultValue): string => {
    if (r === 1) return 'YES';
    if (r === 2) return 'NO';
    if (r === 3) return 'PARTIALLY';
    return '—';
};

const PrintFieldRE = ({ label, value }: { label: string; value?: string }) => (
    <div style={{ marginBottom: '6pt' }}>
        <div style={{ fontSize: '8pt', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
        <div style={{ fontSize: '9.5pt', whiteSpace: 'pre-wrap', borderBottom: '0.5pt solid #555', paddingBottom: '2pt', minHeight: '14pt' }}>
            {value && value.trim() ? value : '—'}
        </div>
    </div>
);

const ReviewerEvalPrintView: React.FC<ReviewerEvalPrintViewProps> = (p) => {
    const sectionHeader = (label: string) => (
        <h3 style={{ fontSize: '10.5pt', fontWeight: 800, textTransform: 'uppercase', borderBottom: '1pt solid #000', paddingBottom: '3pt', marginBottom: '8pt' }}>
            {label}
        </h3>
    );

    return (
        <div className="hidden print:block rev-eval-print-doc" style={{ fontFamily: 'Georgia, "Times New Roman", serif', color: '#000', background: 'white', fontSize: '10pt', lineHeight: 1.4 }}>

            {/* ── HEADER ── */}
            <div className="rev-eval-print-section" style={{ borderBottom: '2pt solid #000', paddingBottom: '8pt', marginBottom: '12pt', textAlign: 'center' }}>
                <p style={{ fontSize: '9pt', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, margin: 0 }}>
                    Tertiary Education Trust Fund — TAG
                </p>
                <h1 style={{ fontSize: '18pt', fontWeight: 800, margin: '4pt 0 2pt 0', lineHeight: 1.1 }}>
                    Evaluation of Reviewer's Assessment Report
                </h1>
                <p style={{ fontSize: '10pt', margin: '0 0 4pt 0', fontStyle: 'italic' }}>{p.titleDisplay}</p>
                <p style={{ fontSize: '9.5pt', marginTop: '4pt', marginBottom: 0 }}>
                    <strong>Submission ID:</strong> {p.submissionNumber}
                </p>
            </div>

            {/* ── MANUSCRIPT IDENTIFICATION ── */}
            <div className="rev-eval-print-section" style={{ marginBottom: '14pt' }}>
                {sectionHeader('Manuscript Identification')}
                <table style={{ width: '100%', fontSize: '9.5pt', borderCollapse: 'collapse' }}>
                    <tbody>
                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top', width: '32%' }}>Name of Author</td><td style={{ padding: '2pt 0' }}>{p.authorName}</td></tr>
                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top' }}>Manuscript Number</td><td style={{ padding: '2pt 0' }}>{p.submissionNumber}</td></tr>
                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top' }}>Title of Manuscript</td><td style={{ padding: '2pt 0' }}>{p.titleDisplay}</td></tr>
                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top' }}>Reviewer's Name</td><td style={{ padding: '2pt 0' }}>{p.reviewerName}</td></tr>
                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top' }}>Thematic Area</td><td style={{ padding: '2pt 0' }}>{p.areaField}</td></tr>
                    </tbody>
                </table>
            </div>

            {/* ── EVALUATION CRITERIA A–E ── */}
            <div style={{ marginBottom: '12pt' }}>
                {sectionHeader('Evaluation of the Reviewer')}
                {CRITERIA_SECTIONS.map(section => {
                    const state = p.criteria.find(c => c.type === section.type);
                    return (
                        <div key={section.type} className="rev-eval-print-section" style={{ marginBottom: '10pt', borderLeft: '2pt solid #000', paddingLeft: '8pt' }}>
                            <h4 style={{ fontSize: '10pt', fontWeight: 800, textTransform: 'uppercase', margin: '0 0 2pt 0' }}>
                                {section.badge}. {section.label}
                            </h4>
                            <p style={{ fontSize: '9pt', fontStyle: 'italic', margin: '0 0 4pt 0' }}>{section.question}</p>
                            <p style={{ fontSize: '9.5pt', margin: '0 0 4pt 0' }}>
                                <strong>Answer:</strong> {resultLabel(state?.result ?? null)}
                            </p>
                            <PrintFieldRE label="Comment" value={state?.comment} />
                        </div>
                    );
                })}
            </div>

            {/* ── SECTION F: CHAPTER-BY-CHAPTER ── */}
            {p.chapters.length > 0 && (
                <div style={{ marginBottom: '12pt' }}>
                    {sectionHeader('F. Chapter-by-Chapter Assessment')}
                    <p style={{ fontSize: '8pt', fontStyle: 'italic', margin: '0 0 6pt 0' }}>
                        Are the reviewer's comments on each chapter and its sub-topics satisfactory?
                    </p>
                    {p.chapters.map((ch, idx) => (
                        <div key={idx} className="rev-eval-print-section" style={{ marginBottom: '8pt', border: '0.75pt solid #000', padding: '6pt 8pt' }}>
                            <h4 style={{ fontSize: '10pt', fontWeight: 800, textTransform: 'uppercase', margin: '0 0 4pt 0' }}>
                                Chapter {ch.chapterNumber}
                            </h4>
                            <p style={{ fontSize: '9.5pt', margin: '0 0 4pt 0' }}>
                                <strong>Answer:</strong> {resultLabel(ch.result)}
                            </p>
                            <PrintFieldRE label="Comment" value={ch.comment} />
                        </div>
                    ))}
                </div>
            )}

            {/* ── G. GENERAL COMMENTS ── */}
            <div className="rev-eval-print-section" style={{ marginBottom: '12pt' }}>
                {sectionHeader('G. General Comments')}
                <PrintFieldRE label="General comments on the reviewer's overall assessment report" value={p.generalComments} />
            </div>

            {/* ── OUTCOME / DISPOSITION ── */}
            <div className="rev-eval-print-section" style={{ marginBottom: '12pt', border: '1pt solid #000', padding: '8pt 10pt' }}>
                {sectionHeader('Outcome / Disposition')}
                <p style={{ fontSize: '8pt', fontStyle: 'italic', margin: '0 0 6pt 0' }}>
                    These two options are mutually exclusive — only one outcome applies.
                </p>
                <table style={{ width: '100%', fontSize: '9.5pt', borderCollapse: 'collapse' }}>
                    <tbody>
                        <tr><td style={{ padding: '3pt 0', fontWeight: 700 }}>Process further to Author</td><td style={{ padding: '3pt 0', textAlign: 'right' }}>{p.processFurtherToAuthor || '—'}</td></tr>
                        <tr style={{ borderTop: '0.5pt solid #999' }}><td style={{ padding: '3pt 0', fontWeight: 700 }}>Return to Reviewer</td><td style={{ padding: '3pt 0', textAlign: 'right' }}>{p.returnToReviewer || '—'}</td></tr>
                    </tbody>
                </table>
            </div>

            {/* ── SIGNATURE ── */}
            <div className="rev-eval-print-section" style={{ marginTop: '18pt', paddingTop: '8pt', borderTop: '1pt solid #000' }}>
                <table style={{ width: '100%', fontSize: '9.5pt' }}>
                    <tbody>
                        <tr>
                            <td style={{ verticalAlign: 'top', paddingRight: '12pt', width: '60%' }}>
                                <p style={{ margin: 0 }}>Evaluator's Signature: ____________________________</p>
                                <p style={{ margin: '6pt 0 0 0' }}>Name: ____________________________</p>
                                <p style={{ margin: '6pt 0 0 0' }}>Date: ____________________________</p>
                            </td>
                            <td style={{ verticalAlign: 'top', borderLeft: '0.5pt solid #999', paddingLeft: '12pt' }}>
                                <p style={{ margin: 0 }}><strong>Outcome:</strong> {
                                    p.processFurtherToAuthor === 'YES' ? 'Process further to Author' :
                                    p.returnToReviewer === 'YES' ? 'Return to Reviewer' : '—'
                                }</p>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

        </div>
    );
};

export default ReviewerEvaluationForm;